// pages/ServicesPage/ServicesPage.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaFileAlt, FaVideo, FaUserGraduate, FaClock, FaExclamationCircle } from 'react-icons/fa';
import axios from 'axios';
import { BOOKABLE_SERVICES } from '../../data/bookableServices';
import { API_BASE } from '../../config/api';
import './css/ServicesPage.css';

const ServicesPage = () => {
  // Start with local defaults, then override from backend so admin can control them
  const [services, setServices] = useState(BOOKABLE_SERVICES);

  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${API_BASE}/api/services`)
      .then((res) => {
        if (!isMounted) return;
        if (res.data && Array.isArray(res.data.services) && res.data.services.length) {
          setServices(res.data.services);
        }
      })
      .catch(() => {
        // Fail silently and keep using local defaults
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const phoneService = services.find((s) => s.id === 'phone') || BOOKABLE_SERVICES.find((s) => s.id === 'phone');
  const zoomService = services.find((s) => s.id === 'zoom') || BOOKABLE_SERVICES.find((s) => s.id === 'zoom');
  const defenseService = services.find((s) => s.id === 'defense') || BOOKABLE_SERVICES.find((s) => s.id === 'defense');

  // Animation Stagger
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="services-page" dir="rtl">
      
      {/* 1. Page Header */}
      <header className="services-header">
        <div className="container text-center relative-z">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            الخدمات والأسعار
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            استثمار ذكي يختصر عليكِ سنوات من الجهد والتشتت.
          </motion.p>
        </div>
      </header>

      {/* 2. Main Services Grid */}
      <section className="services-grid-section">
        <div className="container">
          <motion.div 
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            
            {/* Service 1: Phone Consult */}
            <div className="col-lg-6 col-md-12">
              <motion.div className="service-card" variants={cardVariants}>
                <div className="icon-wrapper">
                  <FaPhoneAlt />
                </div>
                <div className="card-content">
                  <div className="header-row">
                    <h3>{phoneService.name}</h3>
                    <span className="price-tag">{phoneService.price} ريال</span>
                  </div>
                  <p className="duration"><FaClock /> المدة: {phoneService.duration}</p>
                  <p className="description">{phoneService.description}</p>
                  <ul className="feature-list">
                    {phoneService.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <Link to="/booking" state={{ service: phoneService }} className="btn-book">احجزي موعدك</Link>
                </div>
              </motion.div>
            </div>

            {/* Service 2: Zoom Session */}
            <div className="col-lg-6 col-md-12">
              <motion.div className="service-card featured-card" variants={cardVariants}>
                <div className="badge-featured">الأكثر طلبًا</div>
                <div className="icon-wrapper">
                  <FaVideo />
                </div>
                <div className="card-content">
                  <div className="header-row">
                    <h3>{zoomService.name}</h3>
                    <span className="price-tag">{zoomService.morningPrice} - {zoomService.eveningPrice} ريال</span>
                  </div>
                  <p className="duration"><FaClock /> المدة: {zoomService.duration}</p>
                  <p className="description">{zoomService.description}</p>
                  <ul className="feature-list">
                    {zoomService.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <Link to="/booking" state={{ service: zoomService }} className="btn-book btn-fill">احجزي موعدك</Link>
                </div>
              </motion.div>
            </div>

            {/* Service 3: Academic Review */}
            <div className="col-lg-6 col-md-12">
              <motion.div className="service-card" variants={cardVariants}>
                <div className="icon-wrapper">
                  <FaFileAlt />
                </div>
                <div className="card-content">
                  <div className="header-row">
                    <h3>المراجعة والتدقيق الأكاديمي</h3>
                    <span className="price-tag">15 - 20 ريال / صفحة</span>
                  </div>
                  <p className="duration"><FaClock /> المدة: 3 أيام عمل تقريبًا</p>
                  <p className="description">
                    هذا ليس تدقيقًا لغويًا فقط، بل تدقيق <strong>أكاديمي ومنهجي</strong>. 
                    أكشف لك الفجوات بين الفقرات، وأربط الأجزاء ببعضها، وأضع يدي على الخلل.
                  </p>
                  <ul className="pricing-details">
                    <li><strong>15 ريال</strong> للصفحة (اللغة العربية)</li>
                    <li><strong>20 ريال</strong> للصفحة (اللغة الإنجليزية)</li>
                    <li>تنسيق مجاني (خط 14، تباعد 1.5)</li>
                  </ul>
                  <Link to="/contact" className="btn-book">طلب تسعيرة</Link>
                </div>
              </motion.div>
            </div>

            {/* Service 4: Defense Training */}
            <div className="col-lg-6 col-md-12">
              <motion.div className="service-card" variants={cardVariants}>
                <div className="icon-wrapper">
                  <FaUserGraduate />
                </div>
                <div className="card-content">
                  <div className="header-row">
                    <h3>{defenseService.name}</h3>
                    <span className="price-tag">{defenseService.price} ريال</span>
                  </div>
                  <p className="duration"><FaClock /> المدة: {defenseService.duration}</p>
                  <p className="description">{defenseService.description}</p>
                  <ul className="feature-list">
                    {defenseService.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <Link to="/booking" state={{ service: defenseService }} className="btn-book">احجزي الجلسة</Link>
                </div>
              </motion.div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* 3. Important Info & Policy */}
      <section className="policy-section">
        <div className="container">
          <div className="policy-box">
            <h3 className="policy-title"><FaExclamationCircle /> سياسات وتنبيهات هامة</h3>
            
            <div className="row">
              <div className="col-md-6">
                <h4>⏰ أوقات العمل</h4>
                <p>من الساعة 8:00 صباحًا إلى 08:00 مساًء</p>
                <p>يوميًا ما عدا يوم الجمعة</p>
                <p>يُرجى اختيار اليوم والوقت المناسب لكِ من التقويم</p>
                <p>يتم عقد اللقاء عبر برنامج Zoom</p>
                <p>في حال وجود إزعاج، تشويش، أو ظرف طارئ، يتم تقدير ذلك دائمًا بما يضمن جودة الجلسة</p>
                <p className="note">
                  <strong>تنويه:</strong><br />
                  ترتفع الأسعار في أوقات المساء ويوم الجمعة،<br />
                  وفي شهر رمضان تكون قيمة الجلسة 200 ريال.
                </p>
              </div>
              
              <div className="col-md-6">
                <h4>📝 شروط الخدمة</h4>
                <ul>
                  <li>جميع الخدمات مخصصة <strong>للبنات فقط</strong>.</li>
                  <li>الدفع مقدم (تحويل بنكي) لتأكيد الحجز.</li>
                  <li>المبلغ المدفوع غير قابل للاسترجاع.</li>
                  <li>التخصصات المقبولة: العلوم الإنسانية فقط.</li>
                </ul>
              </div>
            </div>
            
            <div className="payment-note text-center mt-4">
              <p>يتم الدفع عبر التحويل لمصرف الإنماء وإرسال الإيصال عبر الواتساب.</p>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default ServicesPage;